import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { BaseResponseData, GearInstance, ProfileApiAddress, ProfileInfoPageProps } from 'types';
import {
  checkIfFullyAuthorized,
  formatServerErrorsForNotification,
  getPassedGearSteps,
  pushSnackbarErrorNotification,
} from 'utils/services';
import {
  getCustomerCategorySizes,
  passUploadItemMedia,
  passAvailableRentDates,
  saveItemAdditionalInfo,
  saveItemDescription,
  saveItemMainInfo,
  saveItemPricing,
  setItemPublishing,
  saveCancellationFee,
  getRawProfileData,
  saveItemPackage,
  saveItemManagement,
  getItemManagementSettings,
  getStoresData,
  saveNewItemData,
} from 'api';
import { Stepper } from 'components/Stepper';
import { Snackbar, SnackbarProps } from 'components/Snackbar';
import {
  getInitialStep,
  getAdditionalInfoStep,
  getAdditionalInfoSellStep,
  getMainInfoStep,
  getUploadPhotoStep,
  getDescriptionStep,
  getPackageStep,
  getRentPricingStep,
  getBlockedDatesStep,
  getScheduledPublicationStep,
  getCancellationFeeStep,
} from './Steps';

import styles from './ItemManagement.module.scss';
import { getOptionsStep, getVariantsStep } from './Steps/additionalInfo';
import { LocationStockProps } from '../../components/LocationsBlock/LocationsBlock';
import { getSellPricingStep } from './Steps/sellPricingStep';
import dayjs from 'dayjs';

export const ItemManagement = ({
  activities,
  brands,
  categories,
  colors,
  dimensionSets,
  gearInstance,
  markets,
  passedSteps: passed = [],
  sizes,
  user,
  step,
}: ProfileInfoPageProps) => {
  const router = useRouter();
  const [snackNotification, setSnackNotification] = useState<SnackbarProps | null>(null);
  const [formNotification, setFormNotification] = useState({});
  const [gear, setGear] = useState(gearInstance);
  const [gearType, setGearType] = useState(gearInstance.type || 'sell');
  const [passedSteps, setPassedSteps] = useState(passed);
  const [steps, setSteps] = useState([] as any[]);
  const maxStepIndex = gearInstance.type === 'rent' ? 7 : 6;
  const initialStepIndex = step || ((gearInstance.id && !gearInstance.step) ? 1 : (passed.length <= maxStepIndex ? (passed.length ? passed.length - 1 : 0) : maxStepIndex));
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [gearCategory, setGearCategory] = useState(gearInstance?.category_id);
  const [filteredSizes, setFilteredSizes] = useState(sizes || []);
  const [addresses, setAddresses] = useState([])
  const [stores, setStores] = useState([]);
  const [locations, setLocations] = useState<LocationStockProps[]>([])
  const [isOptions, setIsOptions] = useState((gear?.options?.length || 0) > 0)
  const [managementSettings, setManagementSettings] = useState({})
  const isFullyAuthorized = checkIfFullyAuthorized(user);

  useEffect(() => {
    if (!isFullyAuthorized)
      router.push(`/sign-in?redirect=/item-management`)
  }, [])

  useEffect(() => {
    getSettings().then();
    setPassedSteps(getPassedGearSteps(gear));
    setGearCategory((currentId) => {
      if (currentId !== gear?.category_id) {
        return gear?.category_id;
      }
      return currentId;
    });
    getLocations().then();
    getStores().then();
  }, [gear]);

  useEffect(() => {
    getSizes(gearCategory).then();
  }, [gearCategory]);

  const getStores = async () => {
    const response = await getStoresData(undefined, []);
    if (response.data) {
      setStores(response.data);
    }
  }

  const getLocations = async () => {
    const response = await getRawProfileData('addresses');
    setAddresses(response.data)
    const hasPrimary = response.data.some((a: ProfileApiAddress) => a.is_primary)
    setLocations(response.data.map((a: ProfileApiAddress, index) => ({
      address: `${a.address}, ${a.city}, ${a.state} ${a.zip}, ${a.country}`,
      customer_address_id: a.id,
      in_stock: gear.locations?.find(l => l.customer_address_id===a.id)?.in_stock>=0
        ? gear.locations?.find(l => l.customer_address_id===a.id)?.in_stock
        : ((!index && !hasPrimary) ? 1 : a.is_primary ? 1 : 0)
    })))
  };

  const getSizes = async (id?: number) => {
    const response = await getCustomerCategorySizes(id || 0);
    setFilteredSizes(response);
  };

  const getSettings = async () => {
    const response = await getItemManagementSettings();
    setManagementSettings(response);
  };

  const setDraftNotification = () => {
    setSnackNotification({
      header: 'Gear profile saved to draft',
    });
  };

  const handleResponse = (response: any, nextStep: number) => {
    setGear(response.data);
    setCurrentStepIndex(nextStep);
  };

  const stepBack = () => {
    if (currentStepIndex) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      router.back();
    }
  };

  const passInitialStep = (data: any) => {
    setGearType(data.type);
    setCurrentStepIndex(1);
    setPassedSteps(['Deal Type', 'Main Info']);
  };

  const setErrorNotification = (response: BaseResponseData<any>): void => {
    pushSnackbarErrorNotification(response, setSnackNotification);
  };

  const passMainInfoStep = async (data: any) => {
    const response = await saveItemManagement({ type: gearType, step: 'photo', ...data }, gear?.id);
    if (response.ok) {
      setGear(response.data);
      setCurrentStepIndex(2);
      window.history.pushState({}, '', `${document.location.origin}/item-management/${response.data.id}`);
    } else {
      setErrorNotification(response);
    }
  };

  const passUploadPhotoStep = async (_: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'description' }, gear?.id);
    if (response.ok) {
      handleResponse(response, 3);
    } else {
      setErrorNotification(response);
    }
  };

  const passDescriptionStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'pricing', ...data }, gear?.id);
    if (response.ok) {
      handleResponse(response, 4);
    } else {
      setErrorNotification(response);
    }
  };

  const passSellPricingStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'additional-info', ...data }, gear?.id);
    if (response.ok) {
      handleResponse(response, 5);
    } else {
      setErrorNotification(response);
    }
  };

  const passSellAdditionalInfoStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'package', ...data }, gear?.id);
    if (response.ok) {
      handleResponse(response, 6);
    } else {
      setErrorNotification(response);
    }
  };

  const passRentAdditionalInfoStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'pricing', ...data }, gear?.id);
    if (response.ok) {
      handleResponse(response, 5);
    } else {
      setErrorNotification(response);
    }
  };

  const passOptionsStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const requestData = {
      options: data.options.map(o => ({
        ...o,
        values: o.values.filter(v => !!v.value)
      }))
    }
    const response = await saveItemAdditionalInfo(requestData, gear?.id);
    if (response.ok) {
      handleResponse(response, 6);
    } else {
      console.log(formatServerErrorsForNotification(response))
      setSnackNotification({
        header: data.options.some(o => !o.name) ? 'Options names are required' : 'At least one option value required for each option',
        mode: 'error'
      })
    }
  };

  const passVariantsStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({ step: 'package', variants: data.variants }, gear?.id);
    if (response.ok) {
      handleResponse(response, 7);
    } else {
      setErrorNotification(response);
    }
  };

  const passPackageStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await saveItemManagement({
      step: 'schedule-publish',
      shipping_dimension_id: data.shipping_dimension_id,
      width: data.width,
      length: data.length,
      height: data.height,
      weight: data.weight,
    }, gear?.id);
    if (response.ok) {
      if(response.data?.posted_at && (new Date()).getTime() > (new Date(response.data?.posted_at)).getTime())
        return router.push(`/my-gear?type=sell&share=${gear.slug}`);
      handleResponse(response, isOptions ? 8 : 7);
    } else {
      setErrorNotification(response);
    }
  };

  const passRentPricingStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const { address, city, end_time, location_note, point, start_time, country, state, zip, apartment } = data;

    let coords = point
    if(!point || !point.lat) {
      const osmResp = await fetch(`https://nominatim.openstreetmap.org/search?street=${address}&city=${city}&state=${state}&country=${country}&postalcode=${zip}&format=json&polygon=1&addressdetails=1`)
      const osmResult = await osmResp.json()
      coords = {
        lat: osmResult[0]?.lat,
        lng: osmResult[0]?.lon,
      }
    }

    const meeting = {
      address,
      city,
      end_time,
      location_note,
      point: coords,
      start_time,
      country,
      state,
      zip,
      apartment,
    };
    const response = await saveItemPricing(data.store_id ? data : { ...data, meeting }, gear?.id);
    if (response.ok) {
      handleResponse(response, 6);
    } else {
      setErrorNotification(response);
    }
  };

  const passBlockedDatesStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response: BaseResponseData<GearInstance> = await passAvailableRentDates(gear?.id, data);
    if (response.ok && response.data?.slug) {
      if(response.data.store_id) {
        saveNewItemData({posted_at: null }, gear?.id)
          .then(() => {
            router.push(`/my-gear?type=rent&share=${gear.slug}`);
          })
      } else
        handleResponse(response, 7)
    } else {
      setErrorNotification(response);
    }
  };

  const passScheduledPublicationStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response = await setItemPublishing(data, gear?.id);
    if (response.ok && response.data?.slug) {
      return router.push(`/my-gear?type=sell&share=${gear.slug}`);
    } else {
      setErrorNotification(response);
    }
  };

  const passCancellationFeeStep = async (data: any, toDraft?: boolean) => {
    if (toDraft) {
      return setDraftNotification();
    }
    const response: BaseResponseData<GearInstance> = await saveCancellationFee(data, gear?.id || '');
    if (response.ok && response.data?.slug) {
      return router.push(`/my-gear?type=rent&share=${gear.slug}`);
    } else {
      setErrorNotification(response);
    }
  };

  useEffect(() => {
    const initialStep = getInitialStep(gear, passInitialStep);
    const mainInfoStep = getMainInfoStep(gear, activities, brands, categories, markets, passMainInfoStep);
    const uploadPhotoStep = getUploadPhotoStep(gear, passUploadPhotoStep);
    const descriptionStep = getDescriptionStep(gear, passDescriptionStep);
    const sellPricingStep = getSellPricingStep(gear, passSellPricingStep, managementSettings.user_accept_offers, managementSettings.min_offer_price_percent, managementSettings.sell_cover_shipping_allowed, formNotification, setSnackNotification);
    const additionalInfoStep = getAdditionalInfoStep(
      gear,
      filteredSizes,
      colors,
      passRentAdditionalInfoStep,
      formNotification,
    );
    const additionalInfoStepSell = getAdditionalInfoSellStep(
      gear,
      filteredSizes,
      colors,
      locations,
      setIsOptions,
      passSellAdditionalInfoStep,
      formNotification,
      setSnackNotification,
    );
    const optionsStep = getOptionsStep(
      gear,
      passOptionsStep,
      formNotification,
    );
    const variantsStep = getVariantsStep(
      gear,
      addresses,
      passVariantsStep,
      managementSettings.user_accept_offers,
      managementSettings.min_offer_price_percent,
      managementSettings.sell_cover_shipping_allowed,
      formNotification,
    );
    const packageStep = getPackageStep(gear, passPackageStep, dimensionSets, setSnackNotification);
    const rentPricingStep = getRentPricingStep(gear, passRentPricingStep, stores);
    const blockedDatesStep = getBlockedDatesStep(gear, passBlockedDatesStep);
    const scheduledPublicationStep = getScheduledPublicationStep(gear, passScheduledPublicationStep);
    const cancellationFeeStep = getCancellationFeeStep(gear, passCancellationFeeStep);

    if (gearType === 'sell') {
      setSteps(isOptions ? [
        initialStep,
        mainInfoStep,
        uploadPhotoStep,
        descriptionStep,
        sellPricingStep,
        optionsStep,
        variantsStep,
        packageStep,
        scheduledPublicationStep,
      ] : [
        initialStep,
        mainInfoStep,
        uploadPhotoStep,
        descriptionStep,
        sellPricingStep,
        additionalInfoStepSell,
        packageStep,
        scheduledPublicationStep,
      ]);
    } else {
      setSteps(gear.account_type==='business' ? [
        initialStep,
        mainInfoStep,
        uploadPhotoStep,
        descriptionStep,
        additionalInfoStep,
        rentPricingStep,
        blockedDatesStep,
      ] : [
        initialStep,
        mainInfoStep,
        uploadPhotoStep,
        descriptionStep,
        additionalInfoStep,
        rentPricingStep,
        blockedDatesStep,
        cancellationFeeStep,
      ]);
    }
  }, [gearType, gear, addresses, formNotification, managementSettings,  isOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Stepper
        contentWrapperStyles={styles.container}
        stepperName={'Create Gear Profile'}
        steps={steps}
        currentStep={steps[currentStepIndex]}
        passedSteps={passedSteps}
        stepBack={stepBack}
        user={user}
        title={'Item Management | SENDY'}
      />
      {snackNotification && <Snackbar {...snackNotification} onClose={() => setSnackNotification(null)} />}
    </>
  );
};
